import { toast } from "sonner";

import { ICompasAIChatAction } from "@/types/compass.types";
import { useEditActionPrompt, useInvalidateCompassChat } from "@/hooks/useCompassChat";
import { useRunCompassActions } from "@/hooks/useCompassActions";
import { useCancelQueuedAction, useDeleteAction } from "@/hooks/useCompassQueue";
import { useDocument } from "@/hooks/useDocument";
import { runToggleEditing, runCancelAction, runExecuteAction } from "./action-item-handlers.utils";
import { getBackendDocumentId, notifyDocumentNotReady } from "@/utils/document-id.utils";

export const useActionItemHandlers = (
  action: ICompasAIChatAction,
  editedPrompt: string,
  setIsEditing: (v: boolean) => void,
  setEditedPrompt: (v: string) => void
) => {
  const { data: doc } = useDocument();
  const editActionMutation = useEditActionPrompt();
  const deleteActionMutation = useDeleteAction();
  const runActionsMutation = useRunCompassActions();
  const cancelActionMutation = useCancelQueuedAction();
  const invalidateCompassChat = useInvalidateCompassChat();

  const handleToggleEditing = () =>
    runToggleEditing({
      action,
      editedPrompt,
      doc,
      setIsEditing,
      editActionMutation,
      invalidateCompassChat,
    });

  const handleDeleteAction = () => {
    const documentId = getBackendDocumentId(doc);
    if (documentId == null) {
      notifyDocumentNotReady("compass");
      return;
    }
    deleteActionMutation.mutate(
      { actionId: action.id, contextId: documentId },
      {
        onSuccess: () => {
          toast.success("Action deleted", { duration: 2000 });
        },
        onError: (error) => {
          console.error("Failed to delete action:", error);
          toast.error("Failed to delete action", { duration: 2000 });
        },
      }
    );
  };

  const cancelAction = () =>
    runCancelAction({
      action,
      doc,
      cancelActionMutation,
      invalidateCompassChat,
    });

  const executeAction = () =>
    runExecuteAction({
      action,
      editedPrompt,
      doc,
      runActionsMutation,
      invalidateCompassChat,
    });

  const handleCancelEditing = () => {
    setEditedPrompt(action.prompt);
    setIsEditing(false);
  };

  return {
    handleToggleEditing,
    handleDeleteAction,
    cancelAction,
    executeAction,
    handleCancelEditing,
    editActionMutation,
  };
};
